import React, { useCallback, useEffect, useMemo, useState } from "react";
import Icon from "../../../components/Icon";
import Skeleton from "../../../components/Skeleton";
import SearchInput from "../../../components/SearchInput";
import DataTable from "../../../components/DataTable";
import {
  StatusBadge,
  getStatusStyle,
  getApplicationStatus,
  getApplicationHealthScore,
} from "../../../components/Badge";
import { EmptyState, ErrorState } from "../../../components/State";
import {
  ChartCard,
  BarChartComponent,
  DonutChart,
} from "../../../components/Charts";
import {
  fetchApplicationHealthAnalytics,
  fetchApplicationTree,
} from "../services/CMDBService";

const pct = (value) => `${Number(value || 0).toFixed(1)}%`;

export default function ApplicationHealthAnalysisView() {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [range, setRange] = useState("7d");
  const [search, setSearch] = useState("");
  const [appPage, setAppPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(
    async (selectedRange = range) => {
      setLoading(true);
      setError(null);

      try {
        const [res, treeData] = await Promise.all([
          fetchApplicationHealthAnalytics(selectedRange).catch(() => null),
          fetchApplicationTree().catch(() => []),
        ]);

        const treeMap = new Map();
        const flattenTree = (items) => {
          (items || []).forEach((item) => {
            if (item && item.id) {
              treeMap.set(String(item.id), item);
              if (item.code) treeMap.set(item.code.toLowerCase(), item);
              if (item.name) treeMap.set(item.name.toLowerCase(), item);
            }
            if (item?.sub_applications?.length) {
              flattenTree(item.sub_applications);
            }
          });
        };
        flattenTree(treeData);

        let mergedApps = [];
        if (
          res?.applications &&
          Array.isArray(res.applications) &&
          res.applications.length > 0
        ) {
          mergedApps = res.applications.map((app) => {
            const rich =
              treeMap.get(String(app.id)) ||
              treeMap.get(app.code?.toLowerCase()) ||
              treeMap.get(app.name?.toLowerCase()) ||
              {};
            return {
              ...app,
              services: rich.services || app.services || [],
              sub_applications:
                rich.sub_applications || app.sub_applications || [],
              services_count:
                rich.services_count ??
                (rich.services || []).length ??
                app.services_count ??
                0,
              open_tickets_count:
                rich.open_tickets_count ?? app.open_tickets_count ?? 0,
            };
          });
        } else if (Array.isArray(treeData) && treeData.length > 0) {
          const flatList = [];
          const collectAll = (nodes) => {
            (nodes || []).forEach((n) => {
              flatList.push(n);
              if (n.sub_applications?.length) collectAll(n.sub_applications);
            });
          };
          collectAll(treeData);
          mergedApps = flatList;
        }

        setData({
          ...res,
          applications: mergedApps,
        });
      } catch {
        setError("Could not load application health analysis.");
      } finally {
        setLoading(false);
      }
    },
    [range],
  );

  useEffect(() => {
    load(range);
  }, [load, range]);

  const allApplications = useMemo(() => data?.applications || [], [data]);

  const applications = useMemo(() => {
    return allApplications.filter((app) => {
      const appStatus = getApplicationStatus(app);
      const matchesSearch =
        !search ||
        app.name.toLowerCase().includes(search.toLowerCase()) ||
        (app.code && app.code.toLowerCase().includes(search.toLowerCase())) ||
        (app.owner_team &&
          app.owner_team.toLowerCase().includes(search.toLowerCase()));

      const matchesStatus =
        statusFilter === "all" ||
        appStatus === statusFilter ||
        (statusFilter === "healthy" &&
          (appStatus === "healthy" || appStatus === "active"));

      const matchesTier =
        tierFilter === "all" ||
        (tierFilter === "root" && app.is_parent) ||
        (tierFilter === "sub" && !app.is_parent);

      return matchesSearch && matchesStatus && matchesTier;
    });
  }, [allApplications, search, statusFilter, tierFilter]);

  const totalApps = allApplications.length;
  const healthyCount = allApplications.filter((a) => {
    const s = getApplicationStatus(a);
    return s === "healthy" || s === "active" || s === "running";
  }).length;
  const degradedCount = allApplications.filter((a) => {
    const s = getApplicationStatus(a);
    return s === "degraded" || s === "warning";
  }).length;
  const criticalCount = allApplications.filter((a) => {
    const s = getApplicationStatus(a);
    return s === "critical" || s === "down";
  }).length;
  const inactiveCount = allApplications.filter((a) => {
    const s = getApplicationStatus(a);
    return s === "inactive" || s === "stopped" || s === "decommissioned";
  }).length;
  const totalServices = allApplications.reduce(
    (acc, a) => acc + ((a.services || []).length || a.services_count || 0),
    0,
  );
  const totalIncidents = allApplications.reduce(
    (acc, a) => acc + (Number(a.open_tickets_count) || 0),
    0,
  );

  const activeApps = useMemo(() => {
    return allApplications.filter((a) => {
      const s = getApplicationStatus(a);
      return s !== "inactive" && s !== "decommissioned";
    });
  }, [allApplications]);

  const calculatedAvailability = useMemo(() => {
    if (activeApps.length === 0) return totalApps > 0 ? 0 : 100;
    const totalHealth = activeApps.reduce(
      (sum, a) => sum + getApplicationHealthScore(a),
      0,
    );
    return Math.round((totalHealth / activeApps.length) * 10) / 10;
  }, [activeApps, totalApps]);

  const overallAvailability =
    data?.overall_availability !== null &&
    data?.overall_availability !== undefined &&
    Number(data.overall_availability) > 0
      ? Number(data.overall_availability)
      : calculatedAvailability;

  const mttrMinutes =
    data?.mttr_minutes !== null && data?.mttr_minutes !== undefined
      ? Number(data.mttr_minutes)
      : null;

  const donut = useMemo(() => {
    return [
      { name: "Healthy", value: healthyCount, color: "#22C55E" },
      { name: "Degraded", value: degradedCount, color: "#F59E0B" },
      { name: "Critical", value: criticalCount, color: "#EF4444" },
      { name: "Inactive", value: inactiveCount, color: "#94A3B8" },
    ].filter((d) => d.value > 0);
  }, [healthyCount, degradedCount, criticalCount, inactiveCount]);

  const rankingData = useMemo(() => {
    return [...allApplications]
      .map((app) => {
        const health = getApplicationHealthScore(app);
        const st = getApplicationStatus(app);
        return {
          name: app.name,
          health,
          color:
            st === "inactive"
              ? "#94A3B8"
              : health >= 90
                ? "#16A34A"
                : health >= 50
                  ? "#D97706"
                  : "#DC2626",
        };
      })
      .sort((a, b) => b.health - a.health)
      .slice(0, 8);
  }, [allApplications]);

  const incidentsData = useMemo(() => {
    const listWithIncidents = allApplications
      .filter((app) => (Number(app.open_tickets_count) || 0) > 0)
      .sort(
        (a, b) =>
          (Number(b.open_tickets_count) || 0) -
          (Number(a.open_tickets_count) || 0),
      )
      .slice(0, 8)
      .map((app) => ({
        name: app.name,
        count: Number(app.open_tickets_count || 0),
        color: "#DC2626",
      }));

    if (listWithIncidents.length > 0) return listWithIncidents;

    return allApplications.slice(0, 5).map((app) => ({
      name: app.name,
      count: 0,
      color: "#16A34A",
    }));
  }, [allApplications]);

  // Root vs Sub Apps SLA Breakdown
  const rootApps = useMemo(
    () => allApplications.filter((a) => a.is_parent),
    [allApplications],
  );
  const subApps = useMemo(
    () => allApplications.filter((a) => !a.is_parent),
    [allApplications],
  );

  const rootHealthy = rootApps.filter((a) => {
    const s = getApplicationStatus(a);
    return s === "healthy" || s === "active";
  }).length;
  const rootIssues = rootApps.filter((a) => {
    const s = getApplicationStatus(a);
    return s === "degraded" || s === "critical";
  }).length;
  const rootHealthyPct = rootApps.length
    ? Math.round((rootHealthy * 100) / rootApps.length)
    : 100;
  const rootIssuesPct = rootApps.length
    ? Math.round((rootIssues * 100) / rootApps.length)
    : 0;

  const subHealthy = subApps.filter((a) => {
    const s = getApplicationStatus(a);
    return s === "healthy" || s === "active";
  }).length;
  const subIssues = subApps.filter((a) => {
    const s = getApplicationStatus(a);
    return s === "degraded" || s === "critical";
  }).length;
  const subHealthyPct = subApps.length
    ? Math.round((subHealthy * 100) / subApps.length)
    : 100;
  const subIssuesPct = subApps.length
    ? Math.round((subIssues * 100) / subApps.length)
    : 0;

  if (error) {
    return (
      <div className="asset-health-analysis">
        <ErrorState message={error} onRetry={() => load(range)} />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="asset-health-analysis">
        <Skeleton height={42} />
        <Skeleton height={420} style={{ marginTop: 16 }} />
      </div>
    );
  }

  if (!data || allApplications.length === 0) {
    return (
      <div className="asset-health-analysis">
        <EmptyState
          icon="layers"
          title="No application health data"
          subtitle="Register applications and link infrastructure port services to see health and SLA metrics."
        />
      </div>
    );
  }

  const appColumns = [
    {
      header: "Application",
      key: "name",
      tip: "Application name and identifier",
      render: (_, app) => (
        <div>
          <div style={{ fontWeight: 600, color: "#1E293B" }}>{app.name}</div>
          {app.code && (
            <div
              style={{
                fontSize: 11,
                color: "#64748B",
                fontFamily: "var(--font-mono, monospace)",
              }}
            >
              {app.code}
            </div>
          )}
        </div>
      ),
    },
    {
      header: "Tier / Hierarchy",
      key: "is_parent",
      tip: "Root platform container vs child microservice application",
      render: (_, app) => (
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            padding: "2px 8px",
            borderRadius: 6,
            background: app.is_parent ? "#F1F5F9" : "#EFF6FF",
            color: app.is_parent ? "#475569" : "#1D4ED8",
          }}
        >
          {app.is_parent ? "Platform Root" : "Sub-Application"}
        </span>
      ),
    },
    {
      header: "Owner Team",
      key: "owner_team",
      tip: "Owning engineering team",
      render: (val) => <span style={{ color: "#475569" }}>{val || "—"}</span>,
    },
    {
      header: "Mapped Services",
      key: "services_count",
      tip: "Count of underlying infrastructure server services and endpoints",
      render: (_, app) => {
        const count = (app.services || []).length || app.services_count || 0;
        return (
          <span>
            <strong style={{ color: "#0F172A" }}>{count}</strong> ports
          </span>
        );
      },
    },
    {
      header: "Open Incidents",
      key: "open_tickets_count",
      tip: "Active unresolved incidents affecting this application",
      render: (val) => (
        <span
          style={{
            fontWeight: 700,
            color: Number(val) > 0 ? "#DC2626" : "#16A34A",
          }}
        >
          {val || 0}
        </span>
      ),
    },
    {
      header: "Health Score",
      key: "health_score",
      tip: "Aggregated health score percentage",
      render: (_, app) => {
        const score = getApplicationHealthScore(app);
        const effectiveStatus = getApplicationStatus(app);
        const statusStyle = getStatusStyle(effectiveStatus);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 60,
                height: 6,
                background: "#E2E8F0",
                borderRadius: 3,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.max(0, Math.min(score, 100))}%`,
                  height: "100%",
                  background: statusStyle.color,
                }}
              />
            </div>
            <span
              style={{
                fontWeight: 600,
                color: "#1E293B",
                fontSize: 12,
              }}
            >
              {effectiveStatus === "inactive" ? "0%" : `${score}%`}
            </span>
          </div>
        );
      },
    },
    {
      header: "Status",
      key: "status",
      tip: "Application health status indicator",
      render: (_, app) => <StatusBadge entity={app} type="application" />,
    },
  ];

  return (
    <div className="asset-health-analysis">
      <header className="asset-health-analysis__header">
        <div>
          <h1>Application Health & SLA Analysis</h1>
          <p>
            Consolidated SLA availability, MTTR, and incident breakdown across
            business applications
          </p>
        </div>
        <span>Last updated: {new Date().toLocaleString()}</span>
      </header>

      {/* Filter Bar */}
      <div
        className="asset-health-analysis__filters"
        style={{ alignItems: "center" }}
      >
        <SearchInput
          size="lg"
          placeholder="Search applications..."
          value={search}
          onChange={(val) => {
            setSearch(val);
            setAppPage(1);
          }}
          style={{ minWidth: 220, width: "auto" }}
        />

        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            setAppPage(1);
          }}
        >
          <option value="all">All Statuses</option>
          <option value="healthy">Healthy Only</option>
          <option value="degraded">Degraded Only</option>
          <option value="critical">Critical Only</option>
          <option value="inactive">Inactive Only</option>
        </select>

        <select
          value={tierFilter}
          onChange={(event) => {
            setTierFilter(event.target.value);
            setAppPage(1);
          }}
        >
          <option value="all">All Hierarchy Tiers</option>
          <option value="root">Platform Roots Only</option>
          <option value="sub">Sub-Applications Only</option>
        </select>

        <select
          value={range}
          onChange={(event) => {
            setRange(event.target.value);
            setAppPage(1);
          }}
        >
          <option value="24h">24 Hours</option>
          <option value="7d">7 Days</option>
          <option value="30d">30 Days</option>
        </select>

        <button type="button" onClick={() => load(range)}>
          ↻&nbsp; Refresh
        </button>
      </div>

      {/* Top Split Grid */}
      <div className="asset-health-analysis__grid">
        {/* Overall SLA & Health Card */}
        <section className="asset-health-analysis__card asset-health-analysis__card--overall">
          <h2>Overall Platform SLA & Health</h2>
          <p>
            Aggregated availability and health status across all business
            applications
          </p>
          <div className="asset-health-analysis__overall-content">
            <div className="asset-health-analysis__donut">
              <DonutChart
                data={donut}
                innerRadius={86}
                outerRadius={126}
                height={330}
                paddingAngle={1}
                centerLabel={{
                  primary: pct(overallAvailability),
                  primarySize: 44,
                  secondary:
                    overallAvailability !== null &&
                    overallAvailability !== undefined
                      ? `● ${overallAvailability >= 90 ? "Healthy" : overallAvailability >= 50 ? "Degraded" : "Critical"}`
                      : undefined,
                  secondaryColor:
                    overallAvailability >= 90
                      ? "#16A34A"
                      : overallAvailability >= 50
                        ? "#D97706"
                        : "#DC2626",
                  sub: "Platform SLA Uptime",
                }}
                tooltipFormatter={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  return (
                    <div className="asset-health-tooltip">
                      <strong>{payload[0].payload.name}</strong>
                      <span>
                        {payload[0].value.toLocaleString()} applications
                      </span>
                    </div>
                  );
                }}
              />
            </div>

            <div className="asset-health-analysis__stat-list">
              <div>
                <Icon name="layers" size={24} color="#1674E8" />
                <section>
                  <strong>{totalApps.toLocaleString()}</strong>
                  <span>Total Applications</span>
                </section>
              </div>
              <div>
                <Icon name="check" size={24} color="#16A34A" />
                <section>
                  <strong>{healthyCount.toLocaleString()}</strong>
                  <span>
                    Healthy ·{" "}
                    {pct(totalApps ? (healthyCount * 100) / totalApps : 100)}
                  </span>
                </section>
              </div>
              <div>
                <Icon name="alert" size={24} color="#D97706" />
                <section>
                  <strong>
                    {(degradedCount + criticalCount).toLocaleString()}
                  </strong>
                  <span>
                    Issues ·{" "}
                    {pct(
                      totalApps
                        ? ((degradedCount + criticalCount) * 100) / totalApps
                        : 0,
                    )}
                  </span>
                </section>
              </div>
              <div>
                <Icon name="server" size={24} color="#7C3AED" />
                <section>
                  <strong>{totalServices.toLocaleString()}</strong>
                  <span>Mapped Port Services</span>
                </section>
              </div>
            </div>
          </div>
        </section>

        {/* MTTR & Incident Breakdown Card */}
        <section className="asset-health-analysis__card">
          <div className="asset-health-analysis__card-heading">
            <div>
              <h2>Application MTTR & Reliability</h2>
              <p>Mean time to resolution and active blast radius scope</p>
            </div>
            <div className="asset-health-analysis__range">
              {["24h", "7d", "30d"].map((value) => (
                <button
                  key={value}
                  className={range === value ? "active" : ""}
                  onClick={() => setRange(value)}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
              marginTop: 16,
            }}
          >
            <div
              style={{
                padding: "18px 20px",
                background: "#F8FBFF",
                borderRadius: 12,
                border: "1px solid #DBE4F1",
              }}
            >
              <div style={{ fontSize: 13, color: "#53658D", fontWeight: 600 }}>
                MTTR (Mean Time to Resolve)
              </div>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: "#1674E8",
                  marginTop: 4,
                }}
              >
                {mttrMinutes !== null ? `${mttrMinutes}m` : "0m"}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "#16A34A",
                  fontWeight: 600,
                  marginTop: 4,
                }}
              >
                {mttrMinutes !== null
                  ? "Average incident resolution time"
                  : "All systems operating within SLA"}
              </div>
            </div>

            <div
              style={{
                padding: "18px 20px",
                background: "#F8FBFF",
                borderRadius: 12,
                border: "1px solid #DBE4F1",
              }}
            >
              <div style={{ fontSize: 13, color: "#53658D", fontWeight: 600 }}>
                Active Incident Tickets
              </div>
              <div
                style={{
                  fontSize: 32,
                  fontWeight: 700,
                  color: totalIncidents > 0 ? "#DC2626" : "#16A34A",
                  marginTop: 4,
                }}
              >
                {totalIncidents}
              </div>
              <div style={{ fontSize: 12, color: "#53658D", marginTop: 4 }}>
                {totalIncidents === 0
                  ? "Zero open incidents"
                  : `Across ${allApplications.length} application tiers`}
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 18,
              padding: "16px",
              background: "#F8FAFC",
              borderRadius: 10,
              border: "1px solid #E2E8F0",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#1E293B",
                marginBottom: 6,
              }}
            >
              Enterprise Observability & Zero-Code Telemetry
            </div>
            <p
              style={{
                margin: 0,
                fontSize: 12,
                color: "#64748B",
                lineHeight: 1.5,
              }}
            >
              Real-time telemetry continuously evaluates port health, listening
              sockets, and process states across all server nodes. When an
              anomaly is detected, AI Root Cause Analysis maps the blast radius
              through the service dependency tree directly to the affected
              business applications.
            </p>
          </div>
        </section>
      </div>

      {/* Visual Analytics Row: Health Ranking & Incidents Breakdown */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
          marginTop: 16,
        }}
      >
        <ChartCard
          title="Application Health Rankings"
          subtitle="Top business services ordered by reliability score"
          className="asset-health-analysis__card"
          height={0}
        >
          <BarChartComponent
            data={rankingData}
            layout="vertical"
            dataKey="health"
            categoryKey="name"
            categoryWidth={140}
            barSize={14}
            margin={{ left: 8, right: 16 }}
            emptyMessage="No application health records available."
          />
        </ChartCard>

        <ChartCard
          title="Open Incidents by Application Domain"
          subtitle="Active ticket load across business service domains"
          className="asset-health-analysis__card"
          height={0}
        >
          <BarChartComponent
            data={incidentsData}
            layout="horizontal"
            dataKey="count"
            categoryKey="name"
            barColor="#DC2626"
            barSize={24}
            emptyMessage="Zero open incidents across applications."
          />
        </ChartCard>
      </div>

      {/* Applications SLA Performance Breakdown & Table */}
      <section className="asset-health-analysis__card asset-health-analysis__availability">
        <h2>Applications SLA & Performance Breakdown</h2>
        <p>
          Status, health score, owner team, and infrastructure service bindings
        </p>

        {/* Visual Availability Bars */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            marginTop: 16,
            marginBottom: 20,
          }}
        >
          <div
            className="asset-health-analysis__availability-row"
            style={{ margin: 0 }}
          >
            <div>
              <strong>Root Platform Applications</strong>
              <span>
                {rootHealthyPct}% Healthy · {rootApps.length} Platforms
              </span>
            </div>
            <div className="asset-health-analysis__bar">
              <i
                style={{
                  width: `${rootHealthyPct}%`,
                }}
              />
              <b
                style={{
                  width: `${rootIssuesPct}%`,
                }}
              />
            </div>
          </div>

          <div
            className="asset-health-analysis__availability-row"
            style={{ margin: 0 }}
          >
            <div>
              <strong>Sub-Applications & Microservices</strong>
              <span>
                {subHealthyPct}% Healthy · {subApps.length} Microservices
              </span>
            </div>
            <div className="asset-health-analysis__bar">
              <i
                style={{
                  width: `${subHealthyPct}%`,
                  background: "#22C55E",
                }}
              />
              <b
                style={{
                  width: `${subIssuesPct}%`,
                  background: "#F59E0B",
                }}
              />
            </div>
          </div>
        </div>

        {/* Detailed Application Table */}
        <DataTable
          columns={appColumns}
          data={applications}
          page={appPage}
          pageSize={10}
          total={applications.length}
          onPageChange={setAppPage}
          emptyTitle="No applications found"
          emptySubtitle="No applications match the search or filter criteria."
          emptyIcon="layers"
          itemLabel="applications"
        />
      </section>
    </div>
  );
}
